"use client";

import { useOptimistic, useTransition } from "react";
import { toggleBookmarkAction } from "@/app/actions/bookmark";
import { useBookmarkStore } from "@/stores/bookmarkStore";
import { MediaHomeCardProps } from "@/modules/home/components/media-list/MediaHomeCard";
import { UserInfo } from "@/types/user";
import { isEmptyObject } from "@/lib/utils";

interface MediaCardStateProps extends MediaHomeCardProps {
  userInfo: UserInfo | null;
  onBookmarkSuccess?: () => void;
}

export function useMediaCardState({ data, userInfo, onRequireLogin, onBookmarkSuccess }: MediaCardStateProps) {
  const [isPending, startTransition] = useTransition();

  const bookmarkStore = useBookmarkStore();
  const id = String(data.id);

  const initial = bookmarkStore.getById(id, data.isMyBookmark);
  const [optimisticBookmark, setOptimisticBookmark] = useOptimistic(
    initial,
    (_prev, next: boolean) => next
  );

  const toggleBookmark = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isEmptyObject(userInfo?.user)) {
      onRequireLogin?.();
      return;
    }

    startTransition(async () => {
      const nextValue = !optimisticBookmark;
      setOptimisticBookmark(nextValue);

      try {
        await toggleBookmarkAction(Number(id));

        bookmarkStore.set(id, nextValue);
        onBookmarkSuccess?.();
      } catch {
        setOptimisticBookmark(initial);
      }
    });
  };

  return {
    optimisticBookmark,
    isPending,
    toggleBookmark,
  };
}
